import { Type } from "lucide-react";
import { motion } from "framer-motion";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useSettings, TextSize } from "@/engine/settingsStore";
import { GoldRule } from "@/components/atlas/AtlasComponents";
import { useTouchLabel } from "@/hooks/useTouchLabel";

/**
 * Quick reading-typography control — a small Type-icon button that opens
 * a popover with a text-size pill row (A- / A / A+). Settings persist via
 * the existing SettingsProvider, so changes here mirror Settings → Display
 * and follow the user across pages.
 *
 * The control is intentionally lightweight (one button, one popover) so
 * it can drop into any header — both the floating GlobalControls and the
 * inline header pattern wire it as the leftmost button.
 */

// previewPx renders each "A" at a size that represents the option
// itself (small < medium < large), independent of the active setting,
// so the pill row self-demonstrates the scale. Exaggerated relative to
// the real root steps (18/19/20px), which are too close to read.
const TEXT_SIZE_OPTIONS: { value: TextSize; label: string; aria: string; previewPx: number }[] = [
  { value: "small", label: "A-", aria: "Small text", previewPx: 13 },
  { value: "medium", label: "A", aria: "Medium text", previewPx: 17 },
  { value: "large", label: "A+", aria: "Large text", previewPx: 21 },
];

interface TypographyControlProps {
  /** Pixel size of the trigger button (square). Defaults to 32 (sm:w-8). */
  size?: "sm" | "md";
  /** Side the popover opens on. Defaults to "bottom". */
  side?: "top" | "right" | "bottom" | "left";
  /** Alignment of the popover edge with the trigger. Defaults to "end". */
  align?: "start" | "center" | "end";
}

export default function TypographyControl({
  size = "sm",
  side = "bottom",
  align = "end",
}: TypographyControlProps) {
  const { settings, update } = useSettings();

  // Fixed px sizing — the header is UI chrome and must NOT scale with
  // the reading text-size setting (which changes the root font-size,
  // and would scale any rem-based w-/h- utility).
  const trigger = size === "md"
    ? "w-[44px] h-[44px] sm:w-[40px] sm:h-[40px]"
    : "w-[40px] h-[40px] sm:w-[36px] sm:h-[36px]";

  const { handlers, labelNode } = useTouchLabel("Reading typography");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <motion.button
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.92 }}
          {...handlers}
          className={`group relative flex items-center justify-center ${trigger} rounded-sm border border-border/40 text-muted-foreground hover:text-foreground hover:border-border transition-all duration-300`}
          title="Reading typography"
          aria-label="Open reading typography controls"
        >
          <Type className="w-[16px] h-[16px]" />
          {labelNode}
        </motion.button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        sideOffset={8}
        className="w-[260px] p-3 z-[210]"
        style={{
          background: "var(--surface-parchment-elevated)",
          border: "1px solid hsl(43, 50%, 35% / 0.4)",
          boxShadow: "0 8px 24px hsl(var(--ink) / 0.6)",
        }}
      >
        {/* Gold hairline along the top edge — matches every other atlas
            popover (HowToPlay, region dropdown, chronicle popup). */}
        <GoldRule intensity="soft" className="absolute left-3 right-3" style={{ top: 0 }} />

        <div className="space-y-3">
          {/* Text size */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Type className="w-3 h-3" style={{ color: "hsl(var(--gold-dim))" }} />
              <span className="font-display text-[10px] uppercase tracking-[0.18em]" style={{ color: "hsl(var(--gold-dim))" }}>
                Text Size
              </span>
            </div>
            <div className="flex gap-1">
              {TEXT_SIZE_OPTIONS.map((opt) => {
                const active = settings.textSize === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => update({ textSize: opt.value })}
                    aria-label={opt.aria}
                    aria-pressed={active}
                    className={`flex flex-1 items-center justify-center py-1.5 rounded-sm font-display leading-none tracking-wide transition-all ${
                      active
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    style={{
                      fontSize: `${opt.previewPx}px`,
                      background: active ? "hsl(var(--primary) / 0.15)" : "var(--surface-cartouche)",
                      border: `1px solid ${active ? "hsl(var(--primary) / 0.45)" : "hsl(43, 50%, 35% / 0.25)"}`,
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

        </div>
      </PopoverContent>
    </Popover>
  );
}
